// src/app/audit/AI/CoPilotV2/components/ChatRoom.js
import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import styled from "styled-components";

import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import ConnectionStatus from "./ConnectionStatus";

import { SAGA_MESSAGE_WEBSOCKET, MESSAGE_WEBSOCKET } from "constants/actions";

const ChatRoom = ({
  web_chat_id,
  chatUserName,
  messages = [],
  wsConnected,
}) => {
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);

  // Selectors
  const wsMessages = useSelector(
    (state) => state.ai_websocketv2?.messages || {},
    shallowEqual
  );
  const wsError = useSelector(
    (state) => state.ai_websocketv2?.error,
    shallowEqual
  );

  // ChatRoom.js message merging logic
  const allMessages = useMemo(() => {
    const messageMap = new Map();

    // Process historical messages first
    messages.forEach((msg) => {
      if (msg.message?.trim()) {
        const msgId = msg.from === "user" ? `usr-${msg.id}` : `ast-${msg.id}`;
        messageMap.set(msgId, {
          ...msg,
          status: msg.from === "user" ? "complete" : msg.status || "complete",
        });
      }
    });

    // Process websocket messages
    Object.entries(wsMessages).forEach(([prefixedId, msg]) => {
      // Skip empty messages
      if (!msg.message?.trim()) return;

      const baseId = msg.id;
      const existingMsg = messageMap.get(prefixedId);

      if (msg.from === "assistant" && msg.status === "streaming") {
        messageMap.set(prefixedId, {
          ...msg,
          message: existingMsg
            ? (existingMsg.message || "") + (msg.message || "")
            : msg.message,
        });
      } else {
        messageMap.set(prefixedId, {
          ...msg,
          status: msg.from === "user" ? "complete" : msg.status,
        });
      }
    });

    // Convert to array and sort by timestamp
    return Array.from(messageMap.values())
      .filter((msg) => msg.message?.trim())
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [messages, wsMessages]);

  const handleSendMessage = useCallback(
    (messageText) => {
      if (!wsConnected || !web_chat_id) {
        console.error("Cannot send message:", {
          connected: wsConnected,
          hasWebChatId: !!web_chat_id,
        });
        return;
      }
      const messagePayload = {
        id: `msg-${Date.now()}`,
        message: messageText.trim(),
        web_chat_id,
        from: "user",
        username: chatUserName,
        timestamp: new Date().toISOString(),
        status: "complete",
      };
      dispatch({
        type: MESSAGE_WEBSOCKET,
        payload: messagePayload,
      });
      dispatch({
        type: SAGA_MESSAGE_WEBSOCKET,
        payload: messagePayload,
      });
    },
    [dispatch, web_chat_id, wsConnected, chatUserName]
  );

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [allMessages]);

  return (
    <ChatContainer>
      {wsError && <ConnectionStatus error>{wsError}</ConnectionStatus>}
      <MessageList>
        {allMessages.map((message) => (
          <ChatMessage
            key={`${message.id}-${message.status}-${message.timestamp}`}
            message={message}
            isStreaming={message.status === "streaming"}
          />
        ))}
        <div ref={messagesEndRef} />
      </MessageList>
      <InputArea>
        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={!wsConnected}
          placeholder={wsConnected ? "Type a message..." : "Connecting..."}
        />
      </InputArea>
    </ChatContainer>
  );
};

export default ChatRoom;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputArea = styled.div`
  padding: 20px;
  border-top: 1px solid #eee;
  background: #ffffff;
`;
